type BoxHeader = {
  offset: number;
  size: number;
  headerSize: number;
  type: string;
};

/** Reads only ISO-BMFF box headers and mvhd, even when moov follows a huge mdat. */
export async function readIsoBmffMovieDuration(
  file: Blob,
): Promise<number | null> {
  const moov = await findBox(file, 0, file.size, "moov");
  if (!moov) return null;
  const mvhd = await findBox(
    file,
    moov.offset + moov.headerSize,
    moov.offset + moov.size,
    "mvhd",
  );
  if (!mvhd) return null;
  const bytes = new Uint8Array(
    await file
      .slice(mvhd.offset, Math.min(mvhd.offset + mvhd.size, mvhd.offset + 48))
      .arrayBuffer(),
  );
  if (bytes.length < mvhd.headerSize + 4) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const version = bytes[mvhd.headerSize];
  let timescale: number;
  let duration: number;
  if (version === 0) {
    if (bytes.length < mvhd.headerSize + 20) return null;
    timescale = view.getUint32(mvhd.headerSize + 12);
    duration = view.getUint32(mvhd.headerSize + 16);
    if (duration === 0xffffffff) return null;
  } else if (version === 1) {
    if (bytes.length < mvhd.headerSize + 32) return null;
    timescale = view.getUint32(mvhd.headerSize + 20);
    const wideDuration = view.getBigUint64(mvhd.headerSize + 24);
    if (wideDuration > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    duration = Number(wideDuration);
  } else {
    return null;
  }
  if (timescale === 0 || duration <= 0) return null;
  return duration / timescale;
}

async function findBox(
  file: Blob,
  start: number,
  end: number,
  wanted: string,
): Promise<BoxHeader | null> {
  let offset = start;
  let boxesRead = 0;
  while (offset + 8 <= end && boxesRead < 100_000) {
    const header = await readBoxHeader(file, offset, end);
    if (!header) return null;
    if (header.type === wanted) return header;
    offset += header.size;
    boxesRead += 1;
  }
  return null;
}

async function readBoxHeader(
  file: Blob,
  offset: number,
  end: number,
): Promise<BoxHeader | null> {
  const bytes = new Uint8Array(
    await file.slice(offset, Math.min(end, offset + 16)).arrayBuffer(),
  );
  if (bytes.length < 8) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const smallSize = view.getUint32(0);
  const type = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
  let size = smallSize;
  let headerSize = 8;
  if (smallSize === 1) {
    if (bytes.length < 16) return null;
    const wideSize = view.getBigUint64(8);
    if (wideSize > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    size = Number(wideSize);
    headerSize = 16;
  } else if (smallSize === 0) {
    size = end - offset;
  }
  if (size < headerSize || offset + size > end) return null;
  return { offset, size, headerSize, type };
}
